import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isDate, isValid, parse, format as formatDate } from 'date-fns';
import { Calendar, CloseIcon, InputMask } from '@repo/atomic-components';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { GreenButton } from '@/ui/components/GreenButton';

// Helpers: parse DD/MM/YYYY into Date, and format Date into DD/MM/YYYY
const parseInputToDate = (value: string): Date | null => {
  // Expect DD/MM/YYYY
  const parsed = parse(value, 'dd/MM/yyyy', new Date());
  return isValid(parsed) ? parsed : null;
};

const formatDateToInput = (d: Date | null): string => {
  if (!d || !isDate(d)) return '';
  return formatDate(d, 'dd/MM/yyyy');
};

interface EditClusterDateDialogProps {
  /** Date the picker opens on — the cluster's current date, or today. */
  initialDate: Date;
  onCancel: () => void;
  onConfirm: (date: Date) => void;
}

/**
 * Draggable date picker used to set a cluster's date. Rendered only while open,
 * so mounting seeds the calendar and the masked input from `initialDate`.
 * Shared by the sections-tree context menu and the thumbnails-bar date pencil.
 */
export const EditClusterDateDialog: FC<EditClusterDateDialogProps> = ({ initialDate, onCancel, onConfirm }) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  // Input mask value (string) that mirrors the calendar selection in DD/MM/YYYY
  const [dateInputValue, setDateInputValue] = useState<string>(() => formatDateToInput(initialDate));

  // Date picker drag state
  const [dialogPos, setDialogPos] = useState({ x: 30, y: 200 });
  const dialogPosRef = useRef(dialogPos);
  dialogPosRef.current = dialogPos;
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    const pos = dialogPosRef.current;
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    const handleDragMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setDialogPos({
        x: dragRef.current.origX + (ev.clientX - dragRef.current.startX),
        y: dragRef.current.origY + (ev.clientY - dragRef.current.startY),
      });
    };
    const handleDragEnd = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selectedDate || !isValid(selectedDate)) return;
    onConfirm(selectedDate);
  }, [onConfirm, selectedDate]);

  // Route Enter to the Confirm button while the dialog is open. A ref holds the
  // latest confirm handler so the listener doesn't re-bind (and lose queued
  // keystrokes) every time selectedDate changes.
  const confirmRef = useRef(handleConfirm);
  confirmRef.current = handleConfirm;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      confirmRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Keep input in sync when selectedDate changes (e.g., via calendar)
  useEffect(() => {
    if (selectedDate !== null) {
      setDateInputValue(formatDateToInput(selectedDate));
    }
  }, [selectedDate]);

  // When the user types a full DD/MM/YYYY (10 chars) update the calendar immediately if valid.
  useEffect(() => {
    const raw = dateInputValue?.trim() ?? '';
    if (raw.length === 10) {
      const parsed = parseInputToDate(raw);
      if (parsed) {
        // If parsed date is different from current selectedDate, update it.
        if (!selectedDate || parsed.getTime() !== selectedDate.getTime()) {
          setSelectedDate(parsed);
        }
      } else {
        // If input is not a valid date, clear selectedDate
        if (selectedDate !== null) {
          setSelectedDate(null);
        }
      }
    } else {
      // If input is not a full date, clear selectedDate
      if (selectedDate !== null) {
        setSelectedDate(null);
      }
    }
    // don't run when editing selectedDate directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateInputValue]);

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left: `${dialogPos.x}px`,
        top: `${dialogPos.y}px`,
        width: '280px',
        background: 'var(--color-fletchers-white)',
        borderRadius: '8px',
        border: '1px solid var(--color-fletchers-gray-110)',
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        padding: '0',
        zIndex: 1000,
        pointerEvents: 'auto',
      }}
    >
      <div
        className="border-fletchers-gray-110 flex cursor-grab items-center justify-between border-b px-[24px] py-[16px] select-none active:cursor-grabbing"
        onMouseDown={handleDragStart}
      >
        <div className="text-fletchers-black-900 text-[18px]/[18px] font-[600]">Edit date</div>
        <button
          type="button"
          aria-label="Close"
          onClick={onCancel}
          className="cursor-pointer rounded p-1 focus:outline-none"
        >
          <CloseIcon width={12} height={12} />
        </button>
      </div>
      <div className="flex w-full flex-col rounded-lg px-[24px] py-[16px]">
        <InputMask
          mask="99/99/9999"
          placeholder="DD/MM/YYYY"
          slotChar="DD/MM/YYYY"
          value={dateInputValue}
          onChange={(e) => {
            const v = e.target?.value ?? '';
            setDateInputValue(v);
          }}
          className="!h-[32px] w-full !text-[14px]"
        />
        <Calendar
          value={selectedDate}
          onChange={(e) => {
            const d = e.value as Date;
            if (isValid(d)) setSelectedDate(d);
          }}
          inline
          showIcon={false}
          dateFormat="yy-mm-dd"
          className="w-full [&_.p-datepicker-calendar-container]:!p-0 [&_.p-datepicker-header]:!p-0 [&_[data-pc-section]]:!px-0 [&_table]:!w-full [&>div]:!w-full [&>div]:!border-0 [&>div]:!p-0"
          pt={{ dayLabel: { style: { height: '30px', width: '32px' } } }}
        />
      </div>
      <div className="flex justify-end gap-2 px-[24px] pb-[24px]">
        <OutlineButton className="!h-[32px]" onClick={onCancel}>
          Cancel
        </OutlineButton>
        <GreenButton className="!h-[32px]" onClick={handleConfirm} disabled={!selectedDate || !isValid(selectedDate)}>
          Confirm
        </GreenButton>
      </div>
    </div>,
    document.body,
  );
};
